// Phase 36 Batch AM — RentCast usage chip.
//
// Renders `47/50 calls remaining` with colour and tooltip cues:
//   • emerald   — plenty of headroom (remaining >= low_threshold)
//   • amber     — low (remaining < low_threshold, > 0)
//   • rose      — exhausted (remaining == 0)
//
// Pure presentation — fetching is the parent's job.

import { Gauge } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function QuotaChip({ quota, className = "" }) {
  if (!quota) return null;
  const remaining = Number(quota.remaining_calls ?? 0);
  const limit = Number(quota.monthly_limit ?? 50);
  const low = !!quota.low;
  const exhausted = remaining <= 0;

  let tone =
    "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (exhausted) tone = "border-rose-200 bg-rose-50 text-rose-700";
  else if (low) tone = "border-amber-200 bg-amber-50 text-amber-700";

  const resetsAt = quota.resets_at
    ? new Date(quota.resets_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="discovery-quota-chip"
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${tone} ${className}`}
          >
            <Gauge className="h-3.5 w-3.5" aria-hidden="true" />
            {remaining}/{limit} calls
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          <div className="space-y-1">
            <p className="font-medium">RentCast market data</p>
            <p className="text-slate-300">
              {exhausted
                ? "Monthly quota exhausted — showing cached results only."
                : low
                  ? "Low quota — new fetches may serve cached results."
                  : "Healthy quota."}
            </p>
            {resetsAt && (
              <p className="text-slate-400">Resets {resetsAt}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
